<template>
  <div class="space-y-8">
    <section class="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-xl shadow-slate-200/50">
      <!-- HEADER -->
      <div class="px-8 py-5 flex flex-wrap items-center justify-between gap-6" style="background-color: #333333;">
          <div class="flex items-center gap-4">
            <div class="p-2 bg-orange-500/10 rounded-lg">
              <v-icon color="orange" small>mdi-handshake-outline</v-icon>
            </div>
            <h2 class="text-white font-black uppercase tracking-[0.2em] text-[11px]">Contratos Vinculados</h2>
          </div>
          
          <div class="flex items-center gap-6 flex-1 justify-end max-w-2xl">
            <v-text-field
              v-model="search"
              placeholder="PESQUISAR CONTRATO..."
              solo flat dense hide-details
              class="custom-field-dark bg-white/10 rounded-2xl w-full max-w-xs"
              prepend-inner-icon="mdi-magnify"
            />
            <button 
              @click="$router.push(`/biddings-contracts/create/${bidding_id}`)"
              class="bg-orange-500 hover:bg-orange-600 text-white font-black text-[10px] px-8 h-12 rounded-xl shadow-xl transition-all flex items-center gap-3 uppercase tracking-widest group active:scale-95"
            >
              <v-icon color="white" size="18" class="group-hover:rotate-90 transition-transform">mdi-plus</v-icon>
              <span>Vincular Contrato</span>
            </button>
          </div>
      </div>

      <div class="p-0">
          <v-data-table
            :headers="headers"
            :items="data"
            :search="search"
            :items-per-page="10"
            :loading="loader"
            loading-text="Sincronizando instrumentos contratuais..."
            class="premium-table"
            hide-default-footer
          >
            <template v-slot:item.contract_number="{ item }">
              <span class="text-[10px] font-black text-slate-400 font-mono tracking-widest bg-slate-50 px-2 py-1 rounded">#{{ item.contract_number }}</span>
            </template>

            <template v-slot:item.provider_name="{ item }">
              <div class="flex flex-col py-3">
                <span class="text-xs font-black text-slate-700 uppercase tracking-tight">{{ item.provider_name }}</span>
                <span class="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{{ item.bidding_process_number || 'PROCESSO NÃO VINCULADO' }}</span>
              </div>
            </template>

            <template v-slot:item.contract_value="{ item }">
              <span class="font-black text-slate-800 text-xs tracking-tighter">R$ {{ realMask(formatCurrencyValue(item.contract_value)) }}</span>
            </template>

            <template v-slot:item.contract_start_term="{ item }">
              <span class="text-[10px] font-bold text-slate-500">{{ item.contract_start_term && formatDateBr(item.contract_start_term) }}</span>
            </template>

            <template v-slot:item.contract_end_term="{ item }">
              <span class="text-[10px] font-bold text-rose-400">{{ item.contract_end_term && formatDateBr(item.contract_end_term) }}</span>
            </template>

            <template v-slot:item.action="{ item }">
              <div class="flex justify-end gap-2 px-4">
                <v-btn icon small class="hover:bg-orange-50 group" @click="$router.push(`/biddings-contracts/edit/${item.contract_id}/${bidding_id}`)" title="Editar">
                  <v-icon size="18" color="orange" class="group-hover:scale-110 transition-transform">mdi-pencil-outline</v-icon>
                </v-btn>
                <v-btn icon small class="hover:bg-red-50 group" @click="deleteItem(item.contract_id)" title="Excluir">
                  <v-icon size="18" color="red" class="group-hover:scale-110 transition-transform">mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="py-20 flex flex-col items-center justify-center text-slate-300">
                <div class="p-6 bg-slate-50 rounded-full mb-4">
                  <v-icon size="64" color="slate-100">mdi-file-cancel-outline</v-icon>
                </div>
                <p class="text-xs font-black uppercase tracking-[0.2em]">Nenhum contrato localizado</p>
              </div>
            </template>
          </v-data-table>
      </div>
    </section>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      timeout="4000"
      top
      elevation="24"
      class="custom-snack"
    >
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="text-xs font-black uppercase tracking-widest">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark icon v-bind="attrs" @click="snack.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  props: ["bidding_id", "prefecture_id"],
  data() {
    return {
      search: "",
      loader: true,
      data: [],
      snack: { show: false, message: "", color: "" },
      headers: [
        { text: "Contrato Nº", value: "contract_number", align: "start", sortable: true },
        { text: "Contratado / Origem", value: "provider_name" },
        { text: "Valor Global", value: "contract_value", align: "start" },
        { text: "Início", value: "contract_start_term", align: "start" },
        { text: "Término", value: "contract_end_term", align: "start" },
        { text: "Ações", align: "right", value: "action", sortable: false }
      ]
    };
  },
  created() {
    this.initialize();
  },
  methods: {
    formatCurrencyValue(value) {
      const numericValue = Number(String(value ?? '').replace(',', '.'));
      return isNaN(numericValue) ? '0.00' : numericValue.toFixed(2);
    },
    realMask(value) {
      value = String(value ?? '0').replace(/\D/g, "");
      value = value.replace(/(\d{1})(\d{14})$/, "$1.$2");
      value = value.replace(/(\d{1})(\d{11})$/, "$1.$2");
      value = value.replace(/(\d{1})(\d{8})$/, "$1.$2");
      value = value.replace(/(\d{1})(\d{5})$/, "$1.$2");
      value = value.replace(/(\d{1})(\d{1,2})$/, "$1,$2");
      return value;
    },
    formatDateBr(date) {
      return date.substr(0, 10).split("-").reverse().join("/");
    },
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", true);
      this.$http.get(`/biddings/contracts/${this.bidding_id}`)
        .then(r => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        })
        .catch(e => {
          this.loader = false; this.snack.show = true; this.snack.message = e.message; this.snack.color = "error"; this.$store.dispatch("setLoader", false);
        });
    },
    deleteItem(id) {
      if (confirm("Deseja rescindir/excluir este contrato?")) {
        this.$store.dispatch("setLoader", true);
        this.$http.delete(`/biddings/contracts/${id}`)
          .then(r => {
            this.snack.show = true; this.snack.message = r.data.result; this.snack.color = "success"; this.initialize();
          })
          .catch(e => {
            this.snack.show = true; this.snack.message = e.message; this.snack.color = "error"; this.$store.dispatch("setLoader", false);
          });
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

.premium-table ::v-deep table { 
  background-color: transparent !important; 
  border-collapse: separate; 
  border-spacing: 0; 
}

.premium-table ::v-deep th { 
  color: #94a3b8 !important; 
  font-weight: 900 !important; 
  text-transform: uppercase !important; 
  font-size: 10px !important; 
  letter-spacing: 0.15em !important;
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 64px !important;
  padding: 0 32px !important;
}

.premium-table ::v-deep td { 
  border-bottom: 1px solid #f8fafc !important;
  color: #334155 !important;
  padding: 12px 32px !important;
}

.premium-table ::v-deep tr:hover {
  background-color: #fcfcfc !important;
}

.custom-field-dark >>> .v-input__control .v-input__slot {
  background: transparent !important;
  font-size: 11px !important;
  font-weight: 900 !important;
  color: #ffffff !important;
  padding: 0 16px !important;
}

.custom-field-dark >>> input {
  color: #ffffff !important;
  text-transform: uppercase;
}

.custom-snack >>> .v-snack__wrapper {
  border-radius: 1.25rem !important;
}
</style>
